import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getBlogTitle, getMetaDescription } from "../../utils/pageTitle";
import {
  Box,
  Container,
  VStack,
  Image,
  Text,
  Button,
  HStack,
  Heading,
  useColorModeValue,
  Badge,
} from "@chakra-ui/react";
import { FaArrowLeft, FaCalendarAlt, FaClock } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

// Dynamically import all blog posts from the posts directory
function importAllPosts() {
  const postContext = require.context('./posts', false, /\.js$/);
  const postsMap = {};
  
  postContext.keys().forEach(key => {
    const post = postContext(key).post;
    if (post && post.slug) {
      postsMap[post.slug] = post;
    }
  });
  
  return postsMap;
}

// Create a map of blog posts by slug
const blogPostsMap = importAllPosts();

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const descriptionColor = useColorModeValue("gray.600", "gray.300");
  const blockquoteTextColor = useColorModeValue("gray.900", "gray.200"); // Darker text for blockquotes in light mode
  const blockquoteBgColor = useColorModeValue("gray.100", "gray.700"); // Slightly darker background for better contrast

  useEffect(() => {
    // Find the blog post with the specified slug
    const foundPost = blogPostsMap[slug];
    if (foundPost) {
      setPost(foundPost);
    } else {
      // Redirect to blog page if post not found
      navigate("/blog");
    }
  }, [slug, navigate]);

  if (!post) {
    return null; // Or a loading spinner
  }
  return (
    <>      <Helmet>
        <title>{getBlogTitle(post.title)}</title>
        <meta name="description" content={getMetaDescription(post.excerpt)} />
      </Helmet>
      <Box pt={{ base: 32, md: 32 }} pb={20} px={4} bg="blue.900" minH="100vh">
        <Container maxW="container.lg">
        <Button
          leftIcon={<FaArrowLeft />}
          variant="ghost"
          color="white"
          mb={8}
          onClick={() => navigate("/blog")}
          _hover={{ bg: "whiteAlpha.200" }}
          as={motion.button}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          Back to Blog
        </Button>

        <MotionVStack
          bg={bgColor}
          borderRadius="lg"
          overflow="hidden"
          boxShadow="xl"
          spacing={6}
          align="start"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <MotionBox
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            w="100%"
          >
            <Image
              src={post.image}
              alt={post.title}
              objectFit="cover"
              w="100%"
              h={{ base: "200px", md: "400px" }}
            />
          </MotionBox>

          <VStack 
            px={{ base: 6, lg: 12 }} 
            pb={8} 
            pt={2} 
            align="start" 
            spacing={4} 
            w="100%"
          >
            <HStack 
              spacing={4} 
              wrap="wrap"
            >
              {Array.isArray(post.category) ? (
                post.category.map((cat, index) => (
                  <Badge key={index} colorScheme="blue" px={3} py={1} fontSize="sm">
                    {cat}
                  </Badge>
                ))
              ) : (
                <Badge colorScheme="blue" px={3} py={1} fontSize="sm">
                  {post.category}
                </Badge>
              )}
              <HStack>
                <FaCalendarAlt color={descriptionColor} />
                <Text color={descriptionColor}>{post.date}</Text>
              </HStack>
              <HStack>
                <FaClock color={descriptionColor} />
                <Text color={descriptionColor}>{post.readTime} min read</Text>
              </HStack>
            </HStack>

            <Heading as="h1" size="2xl" color={textColor} mb={4} fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}>
              {post.title}
            </Heading>

            <Box 
              className="blog-content" 
              w="100%" 
              color={textColor}
            >
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  // Customize the components for the markdown with responsive sizes
                  h2: ({node, ...props}) => (
                    <Heading as="h2" size="xl" mt={6} mb={4} color={textColor} fontSize={{ base: "xl", md: "2xl" }} {...props} />
                  ),
                  h3: ({node, ...props}) => (
                    <Heading as="h3" size="lg" mt={5} mb={3} color={textColor} fontSize={{ base: "lg", md: "xl" }} {...props} />
                  ),
                  h4: ({node, ...props}) => (
                    <Heading as="h4" size="md" mt={4} mb={2} color={textColor} fontSize={{ base: "md", md: "lg" }} {...props} />
                  ),
                  p: ({node, ...props}) => {
                    const textContent = props.children?.toString() || '';
                    const isShort = textContent.length < 150; // Approximate 3 lines worth of text
                    return (
                      <Text 
                        mb={4} 
                        lineHeight="tall" 
                        textAlign={isShort ? "left" : "justify"} 
                        color={textColor} 
                        fontSize={{ base: "sm", md: "md" }} 
                        {...props} 
                      />
                    );
                  },
                  a: ({node, ...props}) => <Text as="a" color="blue.500" {...props} />,
                  ul: ({node, ...props}) => <Box as="ul" pl={4} mb={4} color={textColor} {...props} />,
                  ol: ({node, ...props}) => <Box as="ol" pl={4} mb={4} color={textColor} {...props} />,
                  li: ({node, ...props}) => <Box as="li" mb={1} color={textColor} fontSize={{ base: "sm", md: "md" }} {...props} />,
                  blockquote: ({node, ...props}) => (
                    <Box
                      as="blockquote"
                      borderLeftWidth="4px"
                      borderLeftColor="blue.400"
                      pl={4}
                      pr={4}
                      py={2}
                      my={4}
                      bg={blockquoteBgColor}
                      color={blockquoteTextColor}
                      fontWeight="medium"
                      borderRadius="md"
                      fontSize={{ base: "sm", md: "md" }}
                      sx={{
                        '& em, & p, & strong, & span': {
                          color: 'inherit !important',
                        },
                        '& *': {
                          color: `${blockquoteTextColor} !important`,
                        }
                      }}
                      {...props}
                    />
                  ),
                  em: ({node, ...props}) => (
                    <Text as="em" fontStyle="italic" color="inherit" {...props} />
                  ),
                  strong: ({node, ...props}) => (
                    <Text as="strong" fontWeight="bold" color="inherit" {...props} />
                  ),
                  code: ({node, inline, ...props}) => 
                    inline ? (
                      <Text as="code" bg="gray.100" p={1} borderRadius="sm" color="gray.800" fontSize={{ base: "xs", md: "sm" }} {...props} />
                    ) : (
                      <Box
                        as="pre"
                        p={4}
                        bg="gray.900"
                        color="white"
                        borderRadius="md"
                        overflow="auto"
                        fontSize={{ base: "xs", md: "sm" }}
                        mb={4}
                        {...props}
                      />
                    ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </Box>
          </VStack>        </MotionVStack>
      </Container>
    </Box>
    </>
  );
};

export default BlogDetail;